import { writable, get } from 'svelte/store';
import {
	forgetCurrentUserConsent,
	getQueryClient,
	registerTenantContentReset
} from '$lib/query/client';
import { resetPostHog } from '$lib/services/posthog';
import {
	clearAllDrafts as clearWizardDrafts,
	clearOtherUsersDrafts
} from '$lib/stores/trackerNewWizard';
import { trackerStore } from '$lib/stores/trackers';
import { dataTreeActions } from '$lib/stores/dataTreeStore';
import { getGetCurrentUserInfoApiUsersMeGetQueryOptions } from '$lib/api/generated/users/users';
import type { UserRead } from '$lib/api/generated/schemas';
import { runTenantSessionResets } from '$lib/features/auth/logic/tenantSessionReset';

/**
 * The authenticated user, as the API actually describes it.
 *
 * SAM-1086: this is a narrowing of the generated `UserRead` rather than a
 * parallel hand-written interface. A separate shape is how `kinde_id`, `email`
 * and the two name fields survived here long after the backend stopped
 * serving them — a `Pick` cannot drift, because the contract is the type.
 */
export type User = Pick<
	UserRead,
	'id' | 'timezone' | 'preferred_language' | 'theme_preference' | 'is_active' | 'is_superuser'
>;

export interface AuthState {
	user: User | null;
	token: string | null;
	isAuthenticated: boolean;
	isLoading: boolean;
}

const initialState: AuthState = {
	user: null,
	token: null,
	isAuthenticated: false,
	isLoading: false
};

export const authStore = writable<AuthState>(initialState);

const UNINITIALIZED = Symbol('uninitialized-session');
let activeUserId: string | null | typeof UNINITIALIZED = UNINITIALIZED;
export const sessionEpoch = writable(0);

export interface TenantSessionSnapshot {
	userId: string | null;
	epoch: number;
}

export function captureTenantSession(): TenantSessionSnapshot {
	return { userId: get(authStore).user?.id ?? null, epoch: get(sessionEpoch) };
}

export function isTenantSessionCurrent(snapshot: TenantSessionSnapshot): boolean {
	return (
		get(sessionEpoch) === snapshot.epoch && (get(authStore).user?.id ?? null) === snapshot.userId
	);
}

function clearLegacyTenantStorage(userId: string): void {
	clearOtherUsersDrafts(userId);
	if (typeof localStorage === 'undefined') return;
	for (const key of [
		'tracker_store_state',
		'data-tree-expanded',
		'data-tree-favorites',
		'data-tree-category-favorites'
	]) {
		try {
			localStorage.removeItem(key);
		} catch {
			// Tenant-bearing storage cleanup is best effort; supported state is in memory only.
		}
	}
}

function transitionTenantSession(nextUserId: string | null): void {
	if (activeUserId === UNINITIALIZED && nextUserId !== null) {
		clearLegacyTenantStorage(nextUserId);
		activeUserId = nextUserId;
		return;
	}
	if (activeUserId !== UNINITIALIZED && activeUserId === nextUserId) return;
	resetTenantState();
	activeUserId = nextUserId;
	sessionEpoch.update((epoch) => epoch + 1);
}

export function resetSessionBoundaryForTests(): void {
	activeUserId = UNINITIALIZED;
	sessionEpoch.set(0);
}

function resetTenantState(): void {
	const queryClient = getQueryClient();
	void queryClient.cancelQueries();
	queryClient.clear();
	forgetCurrentUserConsent();
	trackerStore.reset();
	dataTreeActions.reset();
	clearWizardDrafts();
	runTenantSessionResets();
}

function prepareTenantSession(userId: string | null): void {
	transitionTenantSession(userId);
}

export const authActions = {
	// Set authentication state (called from server load functions)
	setAuth: (user: User, token: string) => {
		prepareTenantSession(user.id);
		authStore.update((state) => ({
			...state,
			user,
			token,
			isAuthenticated: true,
			isLoading: false
		}));
	},

	// Clear authentication state
	clearAuth: () => {
		prepareTenantSession(null);
		authStore.set(initialState);
		resetPostHog();
	},

	/**
	 * SML-140: drop every piece of tenant content the client holds (query
	 * cache, tracker store, data tree, drafts, registered tenant resets) while
	 * the same user stays signed in — e.g. after withdrawing health-data
	 * consent, here or in another tab (registered with the query client's
	 * HEALTH_CONSENT_REQUIRED handler below). Bumping the epoch makes in-flight
	 * writes captured before the reset stale (`isTenantSessionCurrent`) and
	 * remounts the session boundary; the active user id is unchanged.
	 */
	resetTenantContent: () => {
		resetTenantState();
		sessionEpoch.update((epoch) => epoch + 1);
	},

	// Logout: clear auth state and redirect to Kinde logout endpoint
	// This performs a full browser redirect to ensure Kinde session is cleared
	logout: () => {
		authActions.clearAuth();
		// Use window.location for full page redirect to ensure Kinde session is cleared
		if (typeof window !== 'undefined') {
			window.location.href = '/api/auth/logout';
		}
	},

	// Set loading state
	setLoading: (loading: boolean) => {
		authStore.update((state) => ({
			...state,
			isLoading: loading
		}));
	},

	// Get current token from store
	getToken: (): string | null => {
		const state = get(authStore);
		return state.token;
	},

	// Set token in store
	setToken: (token: string) => {
		authStore.update((state) => ({
			...state,
			token
		}));
	},

	// Initialize auth state from backend (called when user is authenticated via Kinde)
	// Uses TanStack Query for caching - subsequent calls within staleTime (5 min) will use cache
	initAuth: async () => {
		authActions.setLoading(true);
		try {
			const queryClient = getQueryClient();
			const queryOptions = getGetCurrentUserInfoApiUsersMeGetQueryOptions();

			// Use fetchQuery which respects cache - if data is fresh, returns cached data
			// Otherwise fetches from API and updates cache
			const userRead = await queryClient.fetchQuery(queryOptions);

			// The API no longer serves identity attributes, so the store simply
			// narrows what it receives. No cast, no parallel shape.
			const user: User = {
				id: userRead.id,
				timezone: userRead.timezone || undefined,
				preferred_language: userRead.preferred_language || undefined,
				theme_preference: userRead.theme_preference || undefined,
				is_active: userRead.is_active ?? true,
				is_superuser: userRead.is_superuser ?? false
			};
			prepareTenantSession(user.id);

			// Token will be retrieved by mutator from Kinde session
			authStore.update((state) => ({
				...state,
				user,
				isAuthenticated: true,
				isLoading: false
			}));

			// Replay a locally-stored consent decision into the backend when it has
			// none yet (users who answered the banner before analytics_consent
			// existed). SAM-752. Dynamic import: consent.store.svelte.ts uses runes
			// and must stay out of auth.ts's static import graph — the Orval mutator
			// imports auth.ts, which pure `*.logic.test.ts` files reach transitively
			// (Node env, no Svelte runtime for runes). See preferred_language sync
			// below for the same constraint.
			if (typeof window !== 'undefined') {
				void import('$lib/features/consent/logic/consent.store.svelte')
					.then(({ consentStore }) => {
						consentStore.replayToBackend(userRead.analytics_consent ?? null);
					})
					.catch((e) => console.warn('Failed to replay consent decision:', e));
			}

			// Reconcile the UI language with the stored preference (SAM-836). The
			// backend (users.preferred_language) is the source of truth for a
			// logged-in user: a stored language different from the UI applies
			// itself (setLocale → cookie + one-time reload; happens only on a new
			// device or after clearing cookies). No stored value yet (fresh
			// account) seeds the backend from the current UI locale so server-side
			// AI (weekly report, chat) matches from day one. Detached +
			// browser-only: never blocks or breaks auth, and dynamic imports keep
			// `$app`-free / circular-dep-free static graph.
			if (typeof window !== 'undefined') {
				void (async () => {
					const [{ getLocale, setLocale }, { reconcileLanguage, seedPreferredLanguage }] =
						await Promise.all([
							import('$lib/paraglide/runtime'),
							import('$lib/features/settings/logic/language')
						]);
					const decision = reconcileLanguage(getLocale(), user.preferred_language);
					if (decision.action === 'seedBackend') {
						// seedPreferredLanguage (not persist…): the dynamic imports above
						// can delay this past a user's switcher click, and the seed must
						// never overwrite that fresher choice — it re-checks the store at
						// write time inside the serialized chain.
						await seedPreferredLanguage(decision.locale);
					} else if (decision.action === 'applyStored') {
						setLocale(decision.locale);
					}
				})().catch((e) => console.warn('Failed to reconcile preferred language:', e));
			}
		} catch (error) {
			console.warn('Failed to initialize auth:', error);
			authActions.clearAuth();
		} finally {
			authActions.setLoading(false);
		}
	}
};

registerTenantContentReset(authActions.resetTenantContent, clearWizardDrafts);
